import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Loader2, LogIn, AlertCircle, KeyRound, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Inserisci la tua email');
      return;
    }
    
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success('Email inviata! Controlla la tua casella di posta.');
    } catch (error) {
      // Always show success to prevent email enumeration
      setForgotSent(true);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isLoading) return; // Prevent double submission
    
    setIsLoading(true);
    setErrorMessage(''); // Clear previous error
    
    try {
      const userData = await login(email, password);
      console.log('Login successful:', userData);
      toast.success('Accesso effettuato con successo!');
      
      // Check sessionStorage first, then location state, then default to dashboard
      const savedRedirect = sessionStorage.getItem('redirectAfterLogin');
      const from = savedRedirect || location.state?.from || '/dashboard';
      
      // Clear the saved redirect
      sessionStorage.removeItem('redirectAfterLogin');
      
      // Use React Router navigate for SPA navigation (more reliable on mobile)
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      const errorMsg = error.response?.data?.detail || 'Credenziali non valide. Verifica email e password.';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
      setIsLoading(false);
    }
  };

  const setupAdmin = async () => {
    try {
      await axios.post(`${API}/auth/setup-admin`);
      toast.success('Admin creato! Email: admin@splendorsud.it, Password: admin123');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Admin già esistente');
    }
  };

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1B6B93]/80 to-[#4BA3C7]/60" />
      
      <Card className="w-full max-w-md glass border-white/20 shadow-2xl animate-fade-in relative z-10">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto">
            <img 
              src="https://customer-assets.emergentagent.com/job_5956ea83-268b-47c4-a704-9204051c5002/artifacts/w25p61k6_0splendor%20vettoriale%202020%20S%20filigrana.jpg" 
              alt="Splendor Sud Logo" 
              className="h-20 w-auto mx-auto"
              data-testid="login-logo"
            />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-[#1B6B93]">
              Rapporti Lavoro Straordinario
            </CardTitle>
            <CardDescription className="text-slate-600 mt-2">
              Accedi per gestire i rapporti di lavoro straordinario
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Error Message */}
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm animate-fade-in">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="nome@splendorsud.it"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input-field"
                data-testid="login-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input-field"
                data-testid="login-password-input"
              />
            </div>
            
            {/* Forgot Password Link */}
            <div className="text-right">
              <button
                type="button"
                onClick={() => { setShowForgotPassword(true); setForgotEmail(email); setForgotSent(false); }}
                className="text-sm text-[#1B6B93] hover:text-[#4BA3C7] hover:underline"
              >
                Password dimenticata?
              </button>
            </div>
            
            <Button 
              type="submit" 
              className="w-full btn-primary btn-glow mt-4"
              disabled={isLoading}
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <LogIn className="w-5 h-5 mr-2" />
              )}
              Accedi
            </Button>
          </form>
          
          <div className="mt-6 pt-4 border-t border-slate-200">
            <p className="text-xs text-slate-500 text-center mb-2">
              Prima configurazione?
            </p>
            <Button 
              variant="outline" 
              onClick={setupAdmin}
              className="w-full btn-outline text-sm"
              data-testid="setup-admin-btn"
            >
              Crea Account Admin
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1B6B93] flex items-center gap-2">
              <KeyRound className="w-5 h-5" /> Password Dimenticata
            </DialogTitle>
          </DialogHeader>
          
          {forgotSent ? (
            <div className="text-center py-4">
              <CheckCircle className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Email Inviata!</h3>
              <p className="text-slate-600 text-sm">
                Se l'indirizzo <strong>{forgotEmail}</strong> è registrato, riceverai un'email con le istruzioni per reimpostare la password.
              </p>
              <p className="text-slate-500 text-xs mt-4">
                Controlla anche la cartella spam.
              </p>
              <Button 
                onClick={() => setShowForgotPassword(false)} 
                className="mt-6 btn-primary"
              >
                Chiudi
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-slate-600 text-sm">
                Inserisci l'email del tuo account e ti invieremo un link per reimpostare la password.
              </p>
              
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="nome@splendorsud.it"
                  required
                  className="input-field"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowForgotPassword(false)}
                >
                  Annulla
                </Button>
                <Button 
                  type="submit" 
                  className="btn-primary"
                  disabled={forgotLoading}
                >
                  {forgotLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Invia Email
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
